import { useSearchParams } from "react-router-dom";
import { useCookie } from "../useCookie";
import { useMutation, useQuery, useQueryClient } from "react-query";
import apiService from "../../service/apiService";
import { useSelectedPlace } from "../place/useSelectedPlace";
import { useAuth } from "../useAuth";

const PAGE_SIZE = 10;

export const useActivitiesQuery = (id?: string) => {
  const { token } = useCookie("vToken");
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const page = +(searchParams.get("page") || 1);
  const { user, isAdmin } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const targetPlaceId = isAdmin
    ? undefined
    : (selectedPlaceId ?? user?.adminForPlace?.id);

  const { data, isLoading, isFetching } = useQuery(
    ["getActivities", page, search, targetPlaceId],
    async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        search,
      });

      if (targetPlaceId) {
        params.set("placeId", targetPlaceId);
      }

      const response = await apiService.MakeGetRequest(
        `activities?${params.toString()}`,
        token,
      );
      return response;
    },
    { refetchOnWindowFocus: false },
  );

  const { data: fetchedActivityById } = useQuery(
    ["getActivitiesById", id],
    async () => {
      if (!id) return null;

      const response = await apiService.MakeGetRequest(
        `activities/${id}`,
        token,
      );

      return response;
    },
    {
      enabled: !!id && !!token,
      refetchOnWindowFocus: false,
    },
  );
  const queryClient = useQueryClient();
  const deleteMutation = useMutation(
    (id: string) => {
      const query = targetPlaceId
        ? `?placeId=${encodeURIComponent(targetPlaceId)}`
        : "";
      return apiService.MakeDeleteRequest(`activities/${id}${query}`, token);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("getActivities");
      },
    },
  );

  return {
    Activities: data?.items || [],
    fetchedActivityById,
    fetchingActivities: isLoading || isFetching,
    deleteActivity: deleteMutation.mutate,
    mutating: deleteMutation.isLoading,
    totalActivities: data?.meta.total || 0,
    totalPages: Math.ceil((data?.meta.totalPages || 0) / PAGE_SIZE),
    currentPage: page,
  };
};
